import { runCredits, runStages } from "@reagent/meta";
import { useGameStore } from "../../app/gameStore";
import { useRunStore } from "../../app/runStore";
import "./run-over.css";

/**
 * The end of a run — the shift report.
 *
 * A roguelike's failure screen is read far more often than its victory one, so
 * it gets the same treatment: every batch you ran, what each scored, and what
 * the whole shift came to. The seed is printed because a run is reproducible
 * from it, which makes "that run was absurd" a shareable claim rather than an
 * anecdote.
 */
export function RunOver() {
  const run = useRunStore((s) => s.run);
  const newRun = useRunStore((s) => s.newRun);
  const loadRunStage = useGameStore((s) => s.loadRunStage);

  if (run.status !== "won" && run.status !== "lost") return null;

  const stages = runStages(run);
  const won = run.status === "won";
  const total = runCredits(run);
  const lastResult = run.results[run.results.length - 1];

  const startOver = () => {
    newRun();
    loadRunStage();
  };

  return (
    <div className="run-over">
      <div className={`run-over__panel${won ? " is-won" : " is-lost"}`}>
        <div className="run-over__stamp">{won ? "shift complete" : "shift abandoned"}</div>

        <h1 className="run-over__title">{won ? "Every batch delivered" : "The line went down"}</h1>
        <p className="run-over__detail">
          {won ? (
            `All ${stages.length} syntheses met quota. The shop stays open.`
          ) : (
            <>
              <span className="run-over__detail-stage">
                {lastResult?.stageId.replace(/^stage-\d+-/, "").replace(/-/g, " ") ?? "a batch"}
              </span>{" "}
              was scrapped, and with it the shift.
            </>
          )}
        </p>

        <ol className="run-over__log">
          {stages.map((stage, i) => {
            const result = run.results[i];
            return (
              <li
                key={stage.id}
                className={
                  result ? (result.cleared ? "is-cleared" : "is-failed") : "is-unreached"
                }
              >
                <span className="run-over__log-index">{String(i + 1).padStart(2, "0")}</span>
                <span className="run-over__log-name">{stage.name}</span>
                <span className="run-over__log-score">
                  {result ? `${result.creditsEarned}₵` : "—"}
                </span>
                <span className="run-over__log-note">
                  {result
                    ? result.cleared
                      ? `${result.purity}% pure · ${result.turnsUsed} turns`
                      : "scrapped"
                    : "not reached"}
                </span>
              </li>
            );
          })}
        </ol>

        <div className="run-over__totals">
          <div>
            <dt>shift credits</dt>
            <dd>{total}₵</dd>
          </div>
          <div>
            <dt>deck</dt>
            <dd>{run.deck.length} cards</dd>
          </div>
          <div>
            <dt>upgrades</dt>
            <dd>{run.upgrades.length}</dd>
          </div>
          <div>
            <dt>seed</dt>
            <dd className="run-over__seed">{run.seed}</dd>
          </div>
        </div>

        <button className="run-over__again" onClick={startOver}>
          clock on again
        </button>
      </div>
    </div>
  );
}
